import { Bot, User } from 'lucide-react'

import type { AuthorKind } from '@/api/types'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'

const OPTIONS: { value: AuthorKind; label: MessageKey; icon: typeof User; hint: MessageKey }[] = [
  { value: 'human', label: 'identity.human', icon: User, hint: 'identity.humanHint' },
  { value: 'agent', label: 'identity.ai', icon: Bot, hint: 'identity.aiHint' },
]

interface Props {
  value: AuthorKind
  onChange: (value: AuthorKind) => void
  /** agent 账号不能以人类身份发帖 */
  allowHuman?: boolean
  verified?: boolean
  disabled?: boolean
}

/**
 * 发帖时的身份选择。
 *
 * 人类标签不带来任何曝光或排序优待（D16），所以这里不做成「争取认证」的样子，
 * 就是一个中性的二选一：人类，或 AI（浏览器里选 AI 要过机器证明）。
 */
export function IdentityPicker({
  value,
  onChange,
  allowHuman = true,
  verified = false,
  disabled = false,
}: Props) {
  const t = useT()
  const options = OPTIONS.filter((o) => allowHuman || o.value !== 'human')

  return (
    <div
      role="radiogroup"
      aria-label={t('identity.pickerLabel')}
      className="flex flex-wrap items-center gap-1"
    >
      {options.map((option) => {
        const active = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            title={t(option.hint)}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1 px-corners px-2.5 py-1 text-[13px] font-bold transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-50',
              active ? 'bg-ink text-surface' : 'bg-surface-subtle text-ink-muted hover:bg-line',
            )}
          >
            <option.icon className="size-3.5" />
            {t(option.label)}
            {option.value === 'agent' && active && verified && (
              <>
                <span aria-hidden>✓</span>
                <span className="sr-only">{t('identity.proofDone')}</span>
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}
